import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Tv } from 'lucide-react';

interface NarrativeSlide {
  id: string;
  image: string;
  title: string;
  subtitle: string;
}

interface NarrativeCarouselProps {
  slides: NarrativeSlide[];
  className?: string;
  borderRadius?: 'top' | 'left' | 'all' | 'none';
  autoPlay?: boolean;
}

export function NarrativeCarousel({ slides, className = '', borderRadius = 'top', autoPlay = true }: NarrativeCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-advance slides every 5 seconds
  useEffect(() => {
    if (!autoPlay || slides.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [slides.length, autoPlay]);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % slides.length);
  };

  // Swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      goToNext();
    }

    if (touchStart - touchEnd < -75) {
      goToPrevious();
    }
  };

  if (slides.length === 0) {
    return <NarrativeCarouselEmpty className={className} />;
  }

  // Determine border radius based on prop
  const getBorderRadiusStyle = () => {
    const radius = 'var(--radius-lg)';
    switch (borderRadius) {
      case 'top':
        return { borderTopLeftRadius: radius, borderTopRightRadius: radius };
      case 'left':
        return { borderTopLeftRadius: radius, borderBottomLeftRadius: radius };
      case 'all':
        return { borderRadius: radius };
      case 'none':
        return {};
      default:
        return { borderTopLeftRadius: radius, borderTopRightRadius: radius };
    }
  };

  return (
    <div className={`relative w-full ${className}`}>
      {/* Container - exact radius match with 1px bleed for pixel-perfect corners */}
      <div className="relative overflow-hidden" style={getBorderRadiusStyle()}>
        {/* Slides Container */}
        <div
          ref={containerRef}
          className="relative h-[400px] md:h-[500px]"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {slides.map((slide, index) => (
            <div
              key={slide.id}
              className={`
                absolute inset-0
                transition-all duration-500 ease-in-out
                ${
                  index === currentIndex
                    ? 'opacity-100 translate-x-0'
                    : index < currentIndex
                    ? 'opacity-0 -translate-x-full'
                    : 'opacity-0 translate-x-full'
                }
              `}
            >
              {/* Image */}
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover"
              />

              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

              {/* Text Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 max-w-4xl mx-auto">
                <h2
                  className="text-white mb-2"
                  style={{ fontFamily: 'var(--font-headline)' }}
                >
                  {slide.title}
                </h2>
                <p className="text-white/90 text-lg md:text-xl max-w-2xl">
                  {slide.subtitle}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Navigation Arrows - Desktop Only */}
        <button
          onClick={goToPrevious}
          className="
            hidden md:flex
            absolute left-4 top-1/2 -translate-y-1/2
            w-12 h-12
            items-center justify-center
            rounded-full
            bg-white/20 backdrop-blur-sm
            text-white
            hover:bg-white/30
            transition-all
            z-10
          "
          aria-label="Previous slide"
        >
          <ChevronLeft size={24} />
        </button>

        <button
          onClick={goToNext}
          className="
            hidden md:flex
            absolute right-4 top-1/2 -translate-y-1/2
            w-12 h-12
            items-center justify-center
            rounded-full
            bg-white/20 backdrop-blur-sm
            text-white
            hover:bg-white/30
            transition-all
            z-10
          "
          aria-label="Next slide"
        >
          <ChevronRight size={24} />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2 z-10">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`
                h-1.5 rounded-full transition-all
                ${
                  index === currentIndex
                    ? 'bg-white w-8'
                    : 'bg-white/50 hover:bg-white/75 w-1.5'
                }
              `}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// Empty State
export function NarrativeCarouselEmpty({ className = '' }: { className?: string }) {
  return (
    <div className={`relative w-full ${className}`}>
      <div
        className="
          h-[280px]
          bg-[var(--muted)]
          flex flex-col items-center justify-center
          text-center
          px-6 py-12
        "
      >
        <div className="w-20 h-20 rounded-full bg-[var(--surface)] mb-6 flex items-center justify-center shadow-[var(--elevation-sm)]">
          <Tv size={40} className="text-[var(--text-tertiary)]" />
        </div>
        <h3
          className="text-[var(--text-secondary)] mb-3"
          style={{ fontFamily: 'var(--font-headline)' }}
        >
          No narratives to display
        </h3>
        <p className="text-[var(--text-tertiary)] max-w-md">
          Add inspiring stories and narratives to showcase here
        </p>
      </div>
    </div>
  );
}